import { motion, AnimatePresence } from 'framer-motion'
import { useState, useRef, useEffect } from 'react'
import { lumi } from '@/lib/lumi'
import { notifyFormSubmission } from '@/lib/notifyFormSubmission'
import { usePageMeta } from '@/hooks/usePageMeta'

const ACCENT = '#B9A3D9'
const ACCENT_SOFT = '#d4c5e8'

const SESSAO_URL = 'https://jornada.reacendaluz.com.br/sessao/'
const REATIVE_URL = 'https://jornada.reacendaluz.com.br'

const TOTAL_STEPS = 8

type CountryKey = 'BR' | 'PT' | 'US' | 'OTHER'

const COUNTRIES: { key: CountryKey; label: string; flag: string; code: string }[] = [
  { key: 'BR', label: 'Brasil', flag: '🇧🇷', code: '+55' },
  { key: 'PT', label: 'Portugal', flag: '🇵🇹', code: '+351' },
  { key: 'US', label: 'Estados Unidos', flag: '🇺🇸', code: '+1' },
  { key: 'OTHER', label: 'Outro país', flag: '🌐', code: '' },
]

function parseInitialWhatsapp(v?: string): { country: CountryKey; customCode: string; number: string } {
  if (!v) return { country: 'BR', customCode: '', number: '' }
  const raw = v.trim()
  for (const c of COUNTRIES) {
    if (c.code && raw.startsWith(c.code)) {
      return { country: c.key, customCode: '', number: raw.slice(c.code.length).trim() }
    }
  }
  if (raw.startsWith('+')) {
    const m = raw.match(/^(\+\d{1,4})\s*(.*)$/)
    if (m) return { country: 'OTHER', customCode: m[1], number: m[2] }
  }
  return { country: 'BR', customCode: '', number: raw }
}

// ── Pergunta 3 — o que busca
const P3_OPTS = [
  'Uma clareza pontual sobre algo específico que está me travando agora.',
  'Um processo de transformação mais profundo e acompanhado.',
  'Ainda estou explorando e não sei por onde começar.',
]

// ── Pergunta 4 — contato anterior
const P4_OPTS = [
  'Sim, já fiz o Reative os 4 Elementos.',
  'Sim, já fiz outro processo ou atendimento.',
  'Não, é minha primeira vez.',
]

// ── Pergunta 5 — área (múltipla, até 2)
const P5_OPTS = [
  'Financeiro e prosperidade',
  'Relacionamentos e amor',
  'Saúde e corpo',
  'Propósito e carreira',
  'Autoestima e merecimento',
  'Todas as áreas da vida',
]

// ── Pergunta 6 — prontidão para investir
const P6_OPTS = [
  'Estou pronto e disponível para investir.',
  'Tenho interesse mas preciso entender melhor antes de decidir.',
  'Estou buscando algo gratuito por enquanto.',
]

// ── Lógica de mensagem de confirmação
function getConfirmMessage(p3: string, p6: string): { msg: string; link?: { href: string; label: string } } {
  const isClareza = p3 === P3_OPTS[0]
  const isProcesso = p3 === P3_OPTS[1]
  const isExplorando = p3 === P3_OPTS[2]
  const isPronto = p6 === P6_OPTS[0]
  const isInteresse = p6 === P6_OPTS[1]
  const isGratuito = p6 === P6_OPTS[2]

  if (isClareza && isPronto) {
    return {
      msg: 'Obrigada por compartilhar. Com base no que você descreveu a Sessão de Liberação e Elevação Vibracional foi criada exatamente para esse momento. Acessa aqui e garante a sua:',
      link: { href: SESSAO_URL, label: 'Quero minha Sessão' },
    }
  }
  if (isProcesso && isPronto) {
    return {
      msg: 'Obrigada por compartilhar. Você tem perfil para um processo de transformação real e acompanhado. Entrarei em contato em até 24 horas para conversarmos sobre o melhor caminho para você.',
    }
  }
  if (isGratuito || isExplorando) {
    return {
      msg: 'Obrigada por compartilhar. O ponto de partida perfeito para você é o Reative os 4 Elementos. É gratuito, profundo e vai te mostrar exatamente por onde começar. Acessa aqui:',
      link: { href: REATIVE_URL, label: 'Reative os 4 Elementos' },
    }
  }
  if (isInteresse) {
    return {
      msg: 'Obrigada por compartilhar. Entrarei em contato pessoalmente em até 24 horas para conversar sobre o melhor caminho para você.',
    }
  }
  return {
    msg: 'Obrigada por compartilhar. Entrarei em contato em breve para conversarmos sobre o melhor caminho para você.',
  }
}

// ── Helpers de UI ──────────────────────────────────────────────────────────────

function FieldLabel({ n, children }: { n: number; children: React.ReactNode }) {
  return (
    <label className="block font-body type-body mb-3" style={{ color: 'rgba(232,224,213,0.85)' }}>
      <span className="numerals font-medium" style={{ color: ACCENT_SOFT }}>{n}.</span>{' '}{children}
    </label>
  )
}

function RadioOption({
  value, selected, onClick,
}: { value: string; selected: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="w-full text-left px-4 py-3.5 rounded-sm font-body type-body transition-all duration-200 border"
      style={{
        background: selected ? `${ACCENT}18` : 'rgba(255,255,255,0.03)',
        borderColor: selected ? `${ACCENT}66` : 'rgba(255,255,255,0.08)',
        color: selected ? 'var(--ink, #e8e0d5)' : 'rgba(232,224,213,0.65)',
      }}
    >
      <span
        className="inline-block w-4 h-4 rounded-full border mr-3 flex-shrink-0 align-middle"
        style={{
          borderColor: selected ? ACCENT : 'rgba(255,255,255,0.25)',
          background: selected ? ACCENT : 'transparent',
          boxShadow: selected ? `0 0 0 2px ${ACCENT}33` : 'none',
        }}
      />
      {value}
    </button>
  )
}

function CheckOption({
  value, selected, onClick, disabled,
}: { value: string; selected: boolean; onClick: () => void; disabled: boolean }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled && !selected}
      className="w-full text-left px-4 py-3.5 rounded-sm font-body type-body transition-all duration-200 border disabled:opacity-40 disabled:cursor-not-allowed"
      style={{
        background: selected ? `${ACCENT}18` : 'rgba(255,255,255,0.03)',
        borderColor: selected ? `${ACCENT}66` : 'rgba(255,255,255,0.08)',
        color: selected ? 'var(--ink, #e8e0d5)' : 'rgba(232,224,213,0.65)',
      }}
    >
      <span
        className="inline-block w-4 h-4 rounded-sm border mr-3 flex-shrink-0 align-middle"
        style={{
          borderColor: selected ? ACCENT : 'rgba(255,255,255,0.25)',
          background: selected ? ACCENT : 'transparent',
        }}
      >
        {selected && (
          <svg className="w-full h-full text-[#0a0f14]" viewBox="0 0 16 16" fill="currentColor">
            <path d="M3 8l3.5 3.5L13 5" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        )}
      </span>
      {value}
    </button>
  )
}

function ErrorMsg({ msg }: { msg: string }) {
  return <p className="text-[13px] italic mt-1.5 font-body" style={{ color: 'rgba(220,80,80,0.9)' }}>{msg}</p>
}

// ── Main component ─────────────────────────────────────────────────────────────

export default function PageAtendimento() {
  usePageMeta({
    title: 'Triagem de Atendimento Individual — Reative os 4 Elementos',
    description: 'Responda algumas perguntas para descobrir o melhor caminho de transformação para você agora.',
    ogImage: 'https://static.lumi.new/ab/ab90b76bae5db54d3b7bee0cbc9d9a12.svg',
    ogUrl: 'https://jornada.reacendaluz.com.br/atendimento/',
  })

  const [step, setStep] = useState(1) // 1..8
  const [nome, setNome] = useState('')
  // WhatsApp state
  const [waCountry, setWaCountry] = useState<CountryKey>('BR')
  const [waCustomCode, setWaCustomCode] = useState('')
  const [waNumber, setWaNumber] = useState('')
  const [waOpen, setWaOpen] = useState(false)
  const waDropdownRef = useRef<HTMLDivElement>(null)
  const [email, setEmail] = useState('')
  const [situacao, setSituacao] = useState('')
  const [p3, setP3] = useState('')
  const [p4, setP4] = useState('')
  const [p5, setP5] = useState<string[]>([])
  const [p6, setP6] = useState('')
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [submitting, setSubmitting] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [confirmData, setConfirmData] = useState<{ msg: string; link?: { href: string; label: string } } | null>(null)

  const waActiveCountry = COUNTRIES.find((c) => c.key === waCountry)!
  const waEffectiveCode = waCountry === 'OTHER' ? waCustomCode.trim() : waActiveCountry.code
  const waDigits = waNumber.replace(/\D/g, '')
  const waMinDigits = waCountry === 'BR' ? 10 : 7
  const waCustomCodeValid = waCountry !== 'OTHER' || /^\+\d{1,4}$/.test(waCustomCode.trim())

  const formatBrazil = (v: string) => {
    const d = v.replace(/\D/g, '').slice(0, 11)
    if (d.length <= 2) return d
    if (d.length <= 7) return `(${d.slice(0, 2)}) ${d.slice(2)}`
    return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`
  }
  const formatIntl = (v: string) => v.replace(/\D/g, '').slice(0, 20)

  const handleWaNumberChange = (v: string) => {
    if (waCountry === 'BR') setWaNumber(formatBrazil(v))
    else setWaNumber(formatIntl(v))
  }
  const handleWaCountryChange = (next: CountryKey) => {
    setWaCountry(next)
    if (next === 'BR') setWaNumber((prev) => formatBrazil(prev))
    else setWaNumber((prev) => formatIntl(prev))
  }
  const handleWaCustomCodeChange = (v: string) => {
    let clean = v.replace(/[^\d+]/g, '')
    if (!clean.startsWith('+')) clean = '+' + clean.replace(/\+/g, '')
    clean = '+' + clean.slice(1).replace(/\+/g, '').slice(0, 4)
    setWaCustomCode(clean)
  }

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (waDropdownRef.current && !waDropdownRef.current.contains(e.target as Node)) {
        setWaOpen(false)
      }
    }
    if (waOpen) document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [waOpen])

  function toggleP5(opt: string) {
    setP5(prev => {
      if (prev.includes(opt)) return prev.filter(x => x !== opt)
      if (prev.length >= 2) return prev
      return [...prev, opt]
    })
  }

  function validateStep(s: number): Record<string, string> {
    const e: Record<string, string> = {}
    if (s === 1 && !nome.trim()) e.nome = 'Por favor informe seu nome completo.'
    if (s === 2 && (waDigits.length < waMinDigits || !waCustomCodeValid)) e.whatsapp = 'Por favor informe seu WhatsApp com DDD.'
    if (s === 3 && !email.trim()) e.email = 'Por favor informe seu email.'
    else if (s === 3 && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) e.email = 'Por favor informe um email válido.'
    if (s === 4 && !situacao.trim()) e.situacao = 'Por favor conte o que está acontecendo.'
    if (s === 5 && !p3) e.p3 = 'Por favor escolha uma opção.'
    if (s === 6 && !p4) e.p4 = 'Por favor escolha uma opção.'
    if (s === 7 && p5.length === 0) e.p5 = 'Por favor escolha ao menos uma área.'
    if (s === 8 && !p6) e.p6 = 'Por favor escolha uma opção.'
    return e
  }

  function handleNext() {
    const errs = validateStep(step)
    if (Object.keys(errs).length > 0) {
      setErrors(errs)
      return
    }
    setErrors({})
    if (step < TOTAL_STEPS) {
      setStep(step + 1)
      // scroll suave para o topo da pergunta
      window.scrollTo({ top: 0, behavior: 'smooth' })
    }
  }

  async function handleFinalSubmit() {
    const errs = validateStep(TOTAL_STEPS)
    if (Object.keys(errs).length > 0) {
      setErrors(errs)
      return
    }
    setErrors({})
    setSubmitting(true)
    const fullWhatsapp = `${waEffectiveCode} ${waNumber.trim()}`.trim()
    try {
      const now = new Date().toISOString()
      await lumi.entities.atendimento_triagem.create({
        name: nome.trim(),
        whatsapp: fullWhatsapp,
        email: email.trim(),
        situacao: situacao.trim(),
        p3,
        p4,
        p5,
        p6,
        createdAt: now,
        updatedAt: now,
      })
      notifyFormSubmission({ name: nome.trim(), formName: 'Triagem', submittedAt: now })
      setConfirmData(getConfirmMessage(p3, p6))
      setSubmitted(true)
      window.scrollTo({ top: 0, behavior: 'smooth' })
    } catch (err) {
      console.error('Erro ao enviar formulário:', err)
      setErrors({ submit: 'Ocorreu um erro ao enviar suas respostas. Por favor tente novamente.' })
    } finally {
      setSubmitting(false)
    }
  }

  function handleFormSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (step < TOTAL_STEPS) {
      handleNext()
    } else {
      handleFinalSubmit()
    }
  }

  const progressPct = (step / TOTAL_STEPS) * 100
  const isLastStep = step === TOTAL_STEPS
  const buttonLabel = isLastStep
    ? (submitting ? 'Enviando…' : 'Enviar minhas respostas')
    : 'Continuar'

  const labelNums: Record<number, number> = { 1: 1, 2: 2, 3: 3, 4: 4, 5: 5, 6: 6, 7: 7, 8: 8 }

  return (
    <div className="min-h-screen w-full font-body" style={{ background: '#0a0f14', color: 'var(--ink, #e8e0d5)' }}>

      {/* Header */}
      <header
        className="w-full px-4 py-5 flex items-center justify-center border-b"
        style={{ borderColor: 'rgba(255,255,255,0.06)' }}
      >
        <p className="text-[11px] tracking-[0.35em] uppercase font-body opacity-60" style={{ color: ACCENT_SOFT }}>
          Reacenda Luz · Mônica Maria
        </p>
      </header>

      <main className="w-full max-w-2xl mx-auto px-4 sm:px-6 py-12 md:py-16">
        <AnimatePresence mode="wait">
          {submitted && confirmData ? (
            // ── CONFIRMAÇÃO ──
            <motion.div
              key="confirm"
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
              className="text-center"
            >
              {/* Sigil */}
              <div className="flex justify-center mb-8">
                <svg width="48" height="48" viewBox="0 0 48 48" fill="none" aria-hidden="true">
                  <circle cx="24" cy="24" r="20" stroke={ACCENT} strokeWidth="1.2" opacity="0.55" />
                  <path d="M15 24l6 6 12-12" stroke={ACCENT_SOFT} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>

              <p className="text-[10px] tracking-[0.4em] uppercase font-body mb-5" style={{ color: ACCENT_SOFT }}>
                Recebido com cuidado
              </p>

              <h1 className="font-display type-hero text-[var(--ink,#e8e0d5)] mb-6">
                Obrigada por{' '}
                <em className="italic font-light" style={{ color: ACCENT_SOFT }}>compartilhar</em>
              </h1>

              <div
                className="rounded-sm p-6 sm:p-8 mb-8 text-left"
                style={{
                  background: `linear-gradient(135deg, ${ACCENT}08 0%, rgba(255,255,255,0.02) 100%)`,
                  border: `1px solid ${ACCENT}28`,
                }}
              >
                <p className="font-body type-body leading-relaxed mb-5" style={{ color: 'rgba(232,224,213,0.8)' }}>
                  {confirmData.msg}
                </p>
                {confirmData.link && (
                  <a
                    href={confirmData.link.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-block btn-primary px-6 py-4 min-h-[52px] rounded-sm font-body type-button tracking-[0.1em] uppercase transition-all duration-300 active:scale-[0.98]"
                  >
                    {confirmData.link.label}
                  </a>
                )}
              </div>

              <p
                className="font-display italic text-[16px]"
                style={{ color: `${ACCENT_SOFT}88` }}
              >
                Com amor e presença, Mônica
              </p>
            </motion.div>

          ) : (
            // ── FORMULÁRIO PASSO A PASSO ──
            <motion.div
              key="form"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            >
              {/* Hero */}
              <div className="text-center mb-10">
                <motion.div
                  initial={{ opacity: 0, scale: 0.88 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 1.0, delay: 0.1 }}
                  className="flex justify-center mb-6"
                >
                  <svg width="48" height="48" viewBox="0 0 48 48" fill="none" aria-hidden="true">
                    <circle cx="24" cy="24" r="20" stroke={ACCENT} strokeWidth="1.2" opacity="0.5" />
                    <circle cx="24" cy="24" r="11" stroke={ACCENT_SOFT} strokeWidth="0.7" opacity="0.3" />
                    <circle cx="24" cy="24" r="3" fill={ACCENT} opacity="0.5" />
                  </svg>
                </motion.div>

                <h1 className="font-display type-hero text-[var(--ink,#e8e0d5)] mb-4">
                  Por onde você quer{' '}
                  <em className="italic font-light" style={{ color: ACCENT_SOFT }}>começar?</em>
                </h1>

                {/* Texto introdutório */}
                <div className="max-w-xl mx-auto mt-6 text-left space-y-4">
                  <p
                    className="font-body type-body leading-relaxed"
                    style={{ color: 'rgba(232,224,213,0.85)' }}
                  >
                    Que incrível você chegou até aqui. Isso já é um passo de transformação.
                  </p>
                  <p
                    className="font-body type-body leading-relaxed"
                    style={{ color: 'rgba(232,224,213,0.7)' }}
                  >
                    Para entender qual é o melhor caminho para você agora preencha esse breve formulário. Suas respostas me ajudam a te direcionar com cuidado e presença.
                  </p>
                </div>
              </div>

              {/* Barra de progresso */}
              <div className="mb-10">
                <div className="flex items-center justify-between mb-2.5">
                  <p
                    className="text-[11px] tracking-[0.3em] uppercase font-body"
                    style={{ color: ACCENT_SOFT }}
                  >
                    Pergunta <span className="numerals">{step}</span> de <span className="numerals">{TOTAL_STEPS}</span>
                  </p>
                  <p
                    className="text-[11px] tracking-[0.2em] uppercase font-body opacity-50"
                    style={{ color: ACCENT_SOFT }}
                  >
                    <span className="numerals">{Math.round(progressPct)}</span>%
                  </p>
                </div>
                <div
                  className="w-full h-[3px] rounded-full overflow-hidden"
                  style={{ background: 'rgba(255,255,255,0.06)' }}
                >
                  <motion.div
                    className="h-full rounded-full"
                    style={{
                      background: `linear-gradient(90deg, ${ACCENT} 0%, ${ACCENT_SOFT} 100%)`,
                      boxShadow: `0 0 8px ${ACCENT}55`,
                    }}
                    initial={false}
                    animate={{ width: `${progressPct}%` }}
                    transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                  />
                </div>
              </div>

              {/* Formulário — uma pergunta por vez */}
              <form onSubmit={handleFormSubmit} noValidate>
                <AnimatePresence mode="wait">
                  <motion.div
                    key={`step-${step}`}
                    initial={{ opacity: 0, x: 24 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -24 }}
                    transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
                    className="min-h-[260px]"
                  >
                    {/* ── Pergunta 1 — Nome ── */}
                    {step === 1 && (
                      <div id="field-nome">
                        <FieldLabel n={labelNums[1]}>Qual é o seu nome completo?</FieldLabel>
                        <input
                          type="text"
                          value={nome}
                          autoFocus
                          onChange={e => { setNome(e.target.value); setErrors(p => ({ ...p, nome: '' })) }}
                          placeholder="Seu nome completo"
                          className="input-field w-full px-4 py-3.5 rounded-sm font-body type-body"
                          style={errors.nome ? { borderColor: 'rgba(220,80,80,0.5)' } : {}}
                        />
                        {errors.nome && <ErrorMsg msg={errors.nome} />}
                      </div>
                    )}

                    {/* ── Pergunta 2 — WhatsApp ── */}
                    {step === 2 && (
                      <div id="field-whatsapp">
                        <FieldLabel n={labelNums[2]}>Qual é o seu WhatsApp com DDD?</FieldLabel>
                        <div
                          className={`relative flex items-stretch border rounded-sm transition-colors ${
                            waNumber.trim()
                              ? 'border-[rgba(255,255,255,0.25)] bg-[rgba(255,255,255,0.08)]'
                              : errors.whatsapp
                              ? 'border-[rgba(220,80,80,0.5)] bg-[rgba(220,80,80,0.04)]'
                              : 'border-[rgba(255,255,255,0.15)] bg-[rgba(255,255,255,0.05)]'
                          } focus-within:border-[rgba(255,255,255,0.4)] focus-within:shadow-[0_0_0_2px_rgba(255,255,255,0.08)]`}
                          ref={waDropdownRef}
                        >
                          {/* Seletor de país */}
                          <button
                            type="button"
                            onClick={() => setWaOpen((v) => !v)}
                            aria-haspopup="listbox"
                            aria-expanded={waOpen}
                            className="flex items-center gap-1.5 px-3 py-4 text-[15px] font-body text-[var(--ink-soft)] bg-[var(--ink)]/[0.03] border-r border-[var(--line)] hover:text-[var(--ink)] transition-colors flex-shrink-0"
                          >
                            <span className="text-[15px] leading-none">{waActiveCountry.flag}</span>
                            <span className="tracking-wider numerals text-[13px]">{waEffectiveCode || '+—'}</span>
                            <svg
                              className={`w-3 h-3 text-[var(--ink-soft)] transition-transform duration-200 ${waOpen ? 'rotate-180' : ''}`}
                              viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.5"
                            >
                              <path d="M3 4.5L6 7.5L9 4.5" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                          </button>

                          {/* Campo de número */}
                          <input
                            type="tel"
                            inputMode="numeric"
                            autoFocus
                            value={waNumber}
                            onChange={(e) => { handleWaNumberChange(e.target.value); setErrors(p => ({ ...p, whatsapp: '' })) }}
                            placeholder="número com DDD"
                            className="flex-1 min-w-0 bg-transparent px-3 sm:px-4 py-4 text-[16px] font-body outline-none text-[rgba(255,255,255,0.9)] placeholder:text-[var(--ink-soft)]"
                          />

                          {/* Dropdown de países */}
                          {waOpen && (
                            <div
                              role="listbox"
                              className="absolute left-0 top-full mt-1 w-full sm:w-[280px] overflow-hidden"
                              style={{
                                backgroundColor: '#1a1a24',
                                border: '1px solid rgba(255,255,255,0.12)',
                                borderRadius: '8px',
                                boxShadow: '0 8px 24px rgba(0,0,0,0.5)',
                                zIndex: 9999,
                              }}
                            >
                              {COUNTRIES.map((c) => {
                                const active = waCountry === c.key
                                return (
                                  <button
                                    key={c.key}
                                    type="button"
                                    role="option"
                                    aria-selected={active}
                                    onClick={() => { handleWaCountryChange(c.key); setWaOpen(false) }}
                                    onMouseEnter={(e) => { if (!active) { e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.08)'; e.currentTarget.style.color = 'rgba(255,255,255,1)' } }}
                                    onMouseLeave={(e) => { if (!active) { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.color = 'rgba(255,255,255,0.85)' } }}
                                    className="w-full flex items-center gap-2.5 text-left font-body transition-colors"
                                    style={{
                                      padding: '10px 16px', fontSize: '14px', lineHeight: 1.6,
                                      color: active ? 'rgba(255,255,255,1)' : 'rgba(255,255,255,0.85)',
                                      backgroundColor: active ? 'rgba(255,255,255,0.12)' : 'transparent',
                                    }}
                                  >
                                    <span className="leading-none flex-shrink-0" style={{ fontSize: '16px' }}>{c.flag}</span>
                                    {c.code
                                      ? <span className="numerals tracking-wider flex-shrink-0 min-w-[44px]">{c.code}</span>
                                      : <span className="tracking-wider flex-shrink-0 min-w-[44px]">—</span>}
                                    <span className="truncate">{c.label}</span>
                                  </button>
                                )
                              })}
                            </div>
                          )}
                        </div>

                        {waCountry === 'OTHER' && (
                          <input
                            type="text"
                            inputMode="tel"
                            value={waCustomCode}
                            onChange={(e) => handleWaCustomCodeChange(e.target.value)}
                            placeholder="+00"
                            className="input-field w-full px-4 py-3 text-[15px] font-body rounded-sm mt-2"
                          />
                        )}
                        {errors.whatsapp && <ErrorMsg msg={errors.whatsapp} />}
                      </div>
                    )}

                    {/* ── Pergunta 3 — Email ── */}
                    {step === 3 && (
                      <div id="field-email">
                        <FieldLabel n={labelNums[3]}>Qual é o seu email?</FieldLabel>
                        <input
                          type="email"
                          value={email}
                          autoFocus
                          onChange={e => { setEmail(e.target.value); setErrors(p => ({ ...p, email: '' })) }}
                          placeholder="seu@email.com"
                          className="input-field w-full px-4 py-3.5 rounded-sm font-body type-body"
                          style={errors.email ? { borderColor: 'rgba(220,80,80,0.5)' } : {}}
                        />
                        {errors.email && <ErrorMsg msg={errors.email} />}
                      </div>
                    )}

                    {/* ── Pergunta 4 — Situação ── */}
                    {step === 4 && (
                      <div id="field-situacao">
                        <FieldLabel n={labelNums[4]}>O que te trouxe até aqui? O que está acontecendo na sua vida agora?</FieldLabel>
                        <textarea
                          value={situacao}
                          autoFocus
                          onChange={e => { setSituacao(e.target.value); setErrors(p => ({ ...p, situacao: '' })) }}
                          placeholder="Escreva livremente..."
                          rows={6}
                          className="input-field w-full px-4 py-3.5 rounded-sm font-body type-body resize-none"
                          style={errors.situacao ? { borderColor: 'rgba(220,80,80,0.5)' } : {}}
                        />
                        {errors.situacao && <ErrorMsg msg={errors.situacao} />}
                      </div>
                    )}

                    {/* ── Pergunta 5 ── */}
                    {step === 5 && (
                      <div id="field-p3">
                        <FieldLabel n={labelNums[5]}>O que você está buscando nesse momento?</FieldLabel>
                        <div className="space-y-2.5">
                          {P3_OPTS.map(opt => (
                            <RadioOption
                              key={opt}
                              value={opt}
                              selected={p3 === opt}
                              onClick={() => { setP3(opt); setErrors(p => ({ ...p, p3: '' })) }}
                            />
                          ))}
                        </div>
                        {errors.p3 && <ErrorMsg msg={errors.p3} />}
                      </div>
                    )}

                    {/* ── Pergunta 6 ── */}
                    {step === 6 && (
                      <div id="field-p4">
                        <FieldLabel n={labelNums[6]}>Você já teve contato com o meu trabalho?</FieldLabel>
                        <div className="space-y-2.5">
                          {P4_OPTS.map(opt => (
                            <RadioOption
                              key={opt}
                              value={opt}
                              selected={p4 === opt}
                              onClick={() => { setP4(opt); setErrors(p => ({ ...p, p4: '' })) }}
                            />
                          ))}
                        </div>
                        {errors.p4 && <ErrorMsg msg={errors.p4} />}
                      </div>
                    )}

                    {/* ── Pergunta 7 ── */}
                    {step === 7 && (
                      <div id="field-p5">
                        <FieldLabel n={labelNums[7]}>Qual área da sua vida está pedindo mais atenção agora?</FieldLabel>
                        <p className="font-body text-[12px] italic mb-3" style={{ color: `${ACCENT_SOFT}88` }}>
                          Escolha até <span className="numerals">2</span> opções
                        </p>
                        <div className="space-y-2.5">
                          {P5_OPTS.map(opt => (
                            <CheckOption
                              key={opt}
                              value={opt}
                              selected={p5.includes(opt)}
                              onClick={() => { toggleP5(opt); setErrors(p => ({ ...p, p5: '' })) }}
                              disabled={p5.length >= 2}
                            />
                          ))}
                        </div>
                        {errors.p5 && <ErrorMsg msg={errors.p5} />}
                      </div>
                    )}

                    {/* ── Pergunta 8 ── */}
                    {step === 8 && (
                      <div id="field-p6">
                        <FieldLabel n={labelNums[8]}>Como você se sente em relação a investir na sua transformação agora?</FieldLabel>
                        <div className="space-y-2.5">
                          {P6_OPTS.map(opt => (
                            <RadioOption
                              key={opt}
                              value={opt}
                              selected={p6 === opt}
                              onClick={() => { setP6(opt); setErrors(p => ({ ...p, p6: '' })) }}
                            />
                          ))}
                        </div>
                        {errors.p6 && <ErrorMsg msg={errors.p6} />}
                      </div>
                    )}
                  </motion.div>
                </AnimatePresence>

                {/* Submit error */}
                {errors.submit && (
                  <p className="text-[14px] italic font-body text-center mt-6" style={{ color: 'rgba(220,80,80,0.9)' }}>
                    {errors.submit}
                  </p>
                )}

                {/* CTA */}
                <div className="pt-8">
                  <button
                    type="submit"
                    disabled={submitting}
                    className="w-full btn-primary px-8 py-5 min-h-[60px] rounded-sm font-body type-button tracking-[0.12em] uppercase transition-all duration-300 active:scale-[0.98] disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {buttonLabel}
                  </button>

                  {step > 1 && !submitting && (
                    <button
                      type="button"
                      onClick={() => { setStep(step - 1); setErrors({}); window.scrollTo({ top: 0, behavior: 'smooth' }) }}
                      className="w-full mt-4 px-6 py-3 font-body text-[13px] tracking-[0.2em] uppercase opacity-50 hover:opacity-80 transition-opacity"
                      style={{ color: ACCENT_SOFT }}
                    >
                      ← Voltar
                    </button>
                  )}
                </div>
              </form>
            </motion.div>
          )}
        </AnimatePresence>
      </main>

      {/* Footer */}
      <footer
        className="w-full px-4 py-6 text-center border-t mt-8"
        style={{ borderColor: 'rgba(255,255,255,0.06)' }}
      >
        <p className="text-[10px] tracking-[0.3em] uppercase font-body opacity-40" style={{ color: ACCENT_SOFT }}>
          Reacenda Luz · Mônica Maria
        </p>
      </footer>
    </div>
  )
}
